/**
 * CampaignEditorSkeleton - Skeleton loading UI for the Campaign Editor
 *
 * This component mirrors the actual CampaignEditor layout (popup.jsx):
 * - Header bar with title and Cancel/Save buttons
 * - Tabs row (Content, Rules, Email Templates) + Desktop/Mobile toggle
 * - Left panel: 400px (settings with collapsible sections)
 * - Center panel: flex-grow (canvas preview)
 * - Right panel: 280px (Layers/Properties tabs with tree)
 *
 * Used as Suspense fallback to eliminate spinner cascade and improve
 * perceived performance per Shopify Polaris best practices.
 */
const CampaignEditorSkeleton = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    background: '#fff'
  }}>
    {/* Header with title and action buttons */}
    <div style={{
      padding: '12px 20px',
      borderBottom: '1px solid #dfe3e8',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: '#fff',
      flexShrink: 0
    }}>
      {/* Title skeleton */}
      <div style={{ width: '150px', height: '24px', background: '#f1f2f4', borderRadius: '4px' }} />
      {/* Cancel + Save buttons skeleton */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ width: '70px', height: '32px', background: '#f6f6f7', borderRadius: '8px' }} />
        <div style={{ width: '70px', height: '32px', background: '#008060', borderRadius: '8px', opacity: 0.3 }} />
      </div>
    </div>

    {/* Main content area - 3 columns */}
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left panel - PopupSettings skeleton (400px) */}
      <div style={{
        width: '400px',
        flexShrink: 0,
        borderRight: '1px solid #dfe3e8',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: '#fff'
      }}>
        {/* Tabs + Viewport toggle row */}
        <div style={{
          padding: '8px 16px',
          borderBottom: '1px solid #e1e3e5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          {/* Tabs: Content, Rules, Email Templates */}
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ width: '70px', height: '32px', background: '#f1f2f4', borderRadius: '8px' }} />
            <div style={{ width: '50px', height: '32px', background: '#f6f6f7', borderRadius: '8px' }} />
            <div style={{ width: '100px', height: '32px', background: '#f6f6f7', borderRadius: '8px' }} />
          </div>
          {/* Desktop/Mobile toggle */}
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ width: '36px', height: '32px', background: '#f1f2f4', borderRadius: '8px' }} />
            <div style={{ width: '36px', height: '32px', background: '#f6f6f7', borderRadius: '8px' }} />
          </div>
        </div>

        {/* Settings content skeleton */}
        <div style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Campaign Information section */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f1f2f4'
          }}>
            <div style={{ width: '140px', height: '16px', background: '#f1f2f4', borderRadius: '4px', marginBottom: '12px' }} />
            {/* Campaign name input */}
            <div style={{ height: '36px', background: '#f6f6f7', borderRadius: '8px', marginBottom: '8px' }} />
            {/* Campaign status */}
            <div style={{ width: '100px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
          </div>

          {/* Collapsible sections */}
          {['Campaign Theme', 'Modal Animation', 'Modal Position', 'Overlay Settings', 'Close Button'].map((section, index) => (
            <div
              key={section}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f1f2f4',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{
                width: `${80 + (index * 15)}px`,
                height: '16px',
                background: '#f1f2f4',
                borderRadius: '4px'
              }} />
              {/* Chevron */}
              <div style={{ width: '16px', height: '16px', background: '#f6f6f7', borderRadius: '4px' }} />
            </div>
          ))}

          {/* Conversion Behavior section */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f1f2f4'
          }}>
            <div style={{ width: '140px', height: '16px', background: '#f1f2f4', borderRadius: '4px', marginBottom: '12px' }} />
            <div style={{ height: '36px', background: '#f6f6f7', borderRadius: '8px' }} />
          </div>

          {/* Spacer to push progress to bottom */}
          <div style={{ flex: 1 }} />

          {/* Progress workflow skeleton */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #dfe3e8'
          }}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ width: '24px', height: '24px', background: '#008060', borderRadius: '50%', opacity: 0.3 }} />
              <div style={{ width: '40px', height: '2px', background: '#f1f2f4' }} />
              <div style={{ width: '24px', height: '24px', background: '#f1f2f4', borderRadius: '50%' }} />
              <div style={{ width: '40px', height: '2px', background: '#f1f2f4' }} />
              <div style={{ width: '24px', height: '24px', background: '#f1f2f4', borderRadius: '50%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Center panel - Preview canvas skeleton */}
      <div style={{
        flex: 1,
        background: '#f6f6f7',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: 0,
        padding: '24px'
      }}>
        {/* Popup preview placeholder */}
        <div style={{
          width: '400px',
          minHeight: '450px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* Title skeleton */}
          <div style={{ width: '60%', height: '28px', background: '#f1f2f4', borderRadius: '4px', margin: '0 auto' }} />
          {/* Subtitle skeleton */}
          <div style={{ width: '80%', height: '16px', background: '#f6f6f7', borderRadius: '4px', margin: '0 auto' }} />
          {/* Body text skeleton */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
            <div style={{ width: '100%', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
            <div style={{ width: '90%', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
            <div style={{ width: '75%', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
          </div>
          {/* Input field skeleton */}
          <div style={{
            height: '44px',
            background: '#f6f6f7',
            borderRadius: '8px',
            border: '1px solid #e1e3e5',
            marginTop: '16px'
          }} />
          {/* CTA button skeleton */}
          <div style={{
            height: '44px',
            background: '#008060',
            borderRadius: '8px',
            opacity: 0.3
          }} />
        </div>
      </div>

      {/* Right panel - Layers/Properties tabs with tree */}
      <div style={{
        width: '300px',
        flexShrink: 0,
        borderLeft: '1px solid #dfe3e8',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Tab headers */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e1e3e5',
          flexShrink: 0
        }}>
          <div style={{
            flex: 1,
            padding: '12px 16px',
            borderBottom: '2px solid #008060',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{ width: '50px', height: '14px', background: '#f1f2f4', borderRadius: '4px' }} />
          </div>
          <div style={{
            flex: 1,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{ width: '70px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
          </div>
        </div>

        {/* Tree structure skeleton */}
        <div style={{ padding: '12px', flex: 1 }}>
          {/* Page 1 */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
              <div style={{ width: '16px', height: '16px', background: '#f6f6f7', borderRadius: '4px' }} />
              <div style={{ width: '60px', height: '14px', background: '#f1f2f4', borderRadius: '4px' }} />
            </div>
            {/* Row */}
            <div style={{ marginLeft: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                <div style={{ width: '16px', height: '16px', background: '#f6f6f7', borderRadius: '4px' }} />
                <div style={{ width: '40px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
              </div>
              {/* Elements */}
              <div style={{ marginLeft: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                  <div style={{ width: '14px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
                  <div style={{ width: '50px', height: '12px', background: '#f6f6f7', borderRadius: '4px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                  <div style={{ width: '14px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
                  <div style={{ width: '70px', height: '12px', background: '#f6f6f7', borderRadius: '4px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
                  <div style={{ width: '14px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
                  <div style={{ width: '45px', height: '12px', background: '#f6f6f7', borderRadius: '4px' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Page 2 (collapsed) */}
          <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' }}>
              <div style={{ width: '16px', height: '16px', background: '#f6f6f7', borderRadius: '4px' }} />
              <div style={{ width: '60px', height: '14px', background: '#f6f6f7', borderRadius: '4px' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default CampaignEditorSkeleton;
